// Mitmach-Kalender: der Eingang für Termine aus der Gegend.
//
// Hörer reichen Termine ein, hier werden sie durchgesehen und freigegeben.
// Freigegeben heißt öffentlich: Der Termin steht auf der Hörerseite, liegt im
// Kalender-Abo und taucht im Studio auf.
//
// Die Freigabe ist dieselbe wie in der Redaktion: Wer Beiträge freigeben darf
// (Redaktionsleitung oder Admin), gibt auch Termine frei. Ein zweiter Ablauf mit
// eigenen Rollen wäre für ein kleines Radio nur Ballast.
//
// Vier Fächer: Eingang (die Termine selbst), Kategorien, Serientermine und
// „Für die Sendung“ – der fertige Text zum Ausdrucken oder Ablesen.
//
// Sichtbar ist die Seite nur, wenn das Modul eingeschaltet ist
// (Aussehen → Mitmach-Module).

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  EVENT_STATUS_LABEL,
  nextEventStatus,
  type EventAction,
  type EventStatus,
} from "@webradiosuite/shared/events";

import {
  api,
  eventsApi,
  type EventCategory,
  type EventInput,
  type EventItem,
  type EventRule,
  type EventsOverview,
} from "../api";
import { errorText } from "../components/streaming-ui";
import { EmptyState, Page, PageHeader, Skeleton, Tabs } from "../components/ui";

type Tab = "eingang" | "kategorien" | "serien" | "sendung";

const inputClass = "input font-normal";

// Zustand als Etikett: wartet gelb, freigegeben grün, abgelehnt rot, doppelt grau.
const STATUS_CLASS: Record<EventStatus, string> = {
  pending: "badge-warning",
  approved: "badge-success",
  rejected: "badge-danger",
  merged: "badge-neutral",
};

const WEEKDAYS = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];

function StatusTag({ status }: { status: EventStatus }): JSX.Element {
  return (
    <span className={`badge whitespace-nowrap ${STATUS_CLASS[status]}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
      {EVENT_STATUS_LABEL[status]}
    </span>
  );
}

/** „Sa, 19.09.2026, 20:00“ – kurz, aber mit Wochentag: den sucht man beim Lesen. */
function when(iso: string): string {
  const d = new Date(iso);
  // weekday lässt sich nicht mit dateStyle/timeStyle mischen – der Browser wirft dann
  // „Invalid option“ und die ganze Liste bleibt leer. Darum die Teile einzeln.
  return d.toLocaleString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** Aus einem Zeitpunkt die beiden Felder des Formulars machen. */
function fieldsOf(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, "0");
  return { date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, time: `${pad(d.getHours())}:${pad(d.getMinutes())}` };
}

function emptyInput(): EventInput {
  return { title: "", date: "", time: "", end_time: "", place: "", address: "", organizer: "", description: "", admission: "", link: "", category_id: null };
}

function inputOf(e: EventItem): EventInput {
  const start = fieldsOf(e.starts_at);
  return {
    title: e.title,
    date: start.date,
    time: start.time,
    end_time: e.ends_at ? fieldsOf(e.ends_at).time : "",
    end_date: e.ends_at ? fieldsOf(e.ends_at).date : "",
    place: e.place,
    address: e.address,
    organizer: e.organizer,
    description: e.description,
    admission: e.admission,
    link: e.link,
    category_id: e.category_id,
    contact_name: e.contact_name ?? "",
    contact_mail: e.contact_mail ?? "",
    contact_phone: e.contact_phone ?? "",
  };
}

// ---------------------------------------------------------------- Seite

export function EventsPage(): JSX.Element {
  const [overview, setOverview] = useState<EventsOverview | null>(null);
  const [on, setOn] = useState<boolean | null>(null);
  const [tab, setTab] = useState<Tab>("eingang");
  const [status, setStatus] = useState("pending");
  const [suche, setSuche] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(() => {
    eventsApi.overview({ status, suche })
      .then((data) => { setOverview(data); setError(null); })
      .catch((err) => setError(errorText(err)));
  }, [status, suche]);

  useEffect(() => {
    api.listModules()
      .then((list) => setOn(list.some((m) => m.id === "events" && m.enabled)))
      .catch(() => setOn(false));
  }, []);

  useEffect(() => {
    if (on) reload();
  }, [on, reload]);

  if (on === false) {
    return (
      <Page className="max-w-3xl">
        <PageHeader eyebrow="Hörer" title="Mitmach-Kalender" />
        <EmptyState
          title="„Mitmach-Kalender“ ist nicht eingeschaltet."
          description="Das geht unter Aussehen → Mitmach-Module."
        />
      </Page>
    );
  }
  if (!overview) {
    return (
      <Page className="max-w-5xl">
        {error ? <div className="callout callout-danger">{error}</div> : (
          <div className="space-y-4"><Skeleton className="h-8 w-56" /><Skeleton className="h-4 w-96 max-w-full" /><Skeleton className="h-64" /></div>
        )}
      </Page>
    );
  }

  const tabs: Array<[Tab, string]> = [
    ["eingang", `Eingang${overview.waiting > 0 ? ` (${overview.waiting} warten)` : ""}`],
    ["kategorien", "Kategorien"],
    ["serien", "Serientermine"],
    ["sendung", "Für die Sendung"],
  ];

  return (
    <Page className="max-w-5xl">
      <PageHeader
        eyebrow="Hörer"
        title="Mitmach-Kalender"
        description={<>
          Termine aus der Gegend – eingereicht von Hörern, freigegeben von euch. Was freigegeben ist, steht auf der
          Hörerseite, liegt im Kalender-Abo und erscheint im Studio unter „Termine“.
        </>}
      >
        <div className="-mx-1 overflow-x-auto px-1">
          <Tabs value={tab} onChange={setTab} className="whitespace-nowrap" items={tabs.map(([id, label]) => ({ value: id, label }))} />
        </div>
      </PageHeader>

      <div className="space-y-4">

      {error && <div className="callout callout-danger">{error}</div>}
      {notice && <div className="callout callout-success">{notice}</div>}
      {!overview.may_publish && (
        <div className="callout callout-warning">
          Freigeben darf die Redaktionsleitung. Bearbeiten und vorbereiten könnt ihr alles.
        </div>
      )}

      {tab === "eingang" && (
        <InboxTab
          overview={overview}
          status={status}
          suche={suche}
          onStatus={setStatus}
          onSuche={setSuche}
          onDone={(msg) => { setNotice(msg); reload(); }}
          onError={setError}
        />
      )}
      {tab === "kategorien" && <CategoriesTab overview={overview} onDone={(msg) => { setNotice(msg); reload(); }} onError={setError} />}
      {tab === "serien" && <SeriesTab overview={overview} onDone={(msg) => { setNotice(msg); reload(); }} onError={setError} />}
      {tab === "sendung" && <BroadcastTab onError={setError} />}
      </div>
    </Page>
  );
}

// ---------------------------------------------------------------- Eingang

interface TabProps {
  overview: EventsOverview;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}

function InboxTab({ overview, status, suche, onStatus, onSuche, onDone, onError }: TabProps & {
  status: string;
  suche: string;
  onStatus: (value: string) => void;
  onSuche: (value: string) => void;
}): JSX.Element {
  const [editing, setEditing] = useState<string | "neu" | null>(null);

  return (
    <div className="space-y-3">
      <div className="card flex flex-wrap items-end gap-3 p-3 sm:p-4">
        <label className="text-sm">
          <span className="field-label">Zustand</span>
          <select className={inputClass} value={status} onChange={(e) => onStatus(e.target.value)}>
            <option value="pending">Wartet auf Freigabe</option>
            <option value="approved">Freigegeben</option>
            <option value="rejected">Abgelehnt</option>
            <option value="merged">Doppelt</option>
            <option value="alle">alle</option>
          </select>
        </label>
        <label className="min-w-0 flex-1 basis-48 text-sm">
          <span className="field-label">Suche</span>
          <input className={inputClass} value={suche} onChange={(e) => onSuche(e.target.value)} placeholder="Titel, Ort oder Veranstalter" />
        </label>
        {overview.may_edit && (
          <button
            className="btn btn-primary"
            onClick={() => setEditing(editing === "neu" ? null : "neu")}
          >
            {editing === "neu" ? "Abbrechen" : "Termin anlegen"}
          </button>
        )}
      </div>

      {editing === "neu" && (
        <div className="card animate-fade-in p-5">
          <div className="card-title mb-3">Neuer Termin</div>
          <EventForm
            categories={overview.categories}
            value={emptyInput()}
            onCancel={() => setEditing(null)}
            onSave={async (input) => {
              await eventsApi.create(input);
              setEditing(null);
              onDone("Termin angelegt.");
            }}
            onError={onError}
          />
        </div>
      )}

      {overview.events.length === 0 ? (
        <EmptyState
          title="Hier steht gerade nichts."
          description="Termine kommen über den Block „Termine einreichen“ auf der Hörerseite herein."
        />
      ) : (
        <ul className="card divide-y divide-line overflow-hidden">
          {overview.events.map((e) => (
            <EventRow
              key={e.id}
              event={e}
              overview={overview}
              open={editing === e.id}
              onToggle={() => setEditing(editing === e.id ? null : e.id)}
              onDone={onDone}
              onError={onError}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function EventRow({ event, overview, open, onToggle, onDone, onError }: {
  event: EventItem;
  overview: EventsOverview;
  open: boolean;
  onToggle: () => void;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [dupes, setDupes] = useState<Array<EventItem & { similarity: number }> | null>(null);

  const rights = { mayPublish: overview.may_publish };
  const can = (action: EventAction): boolean => nextEventStatus(event.status, action, rights).ok;

  const run = async (job: () => Promise<unknown>, message: string): Promise<void> => {
    setBusy(true);
    try {
      await job();
      onDone(message);
    } catch (err) {
      onError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className={`px-4 py-3 transition-colors ${open ? "bg-surface-sunken" : "hover:bg-surface-hover"}`}>
      <div className="flex flex-wrap items-center gap-3">
        {/* Datum als Kalenderblatt – das sucht man beim Durchsehen zuerst */}
        <div className="flex h-12 w-12 flex-shrink-0 flex-col items-center justify-center rounded-lg border border-line bg-card leading-none shadow-sm" aria-hidden="true">
          <span className="text-[0.6rem] font-semibold uppercase text-brand-primary">{new Date(event.starts_at).toLocaleDateString("de-DE", { month: "short" })}</span>
          <span className="mt-0.5 text-lg font-semibold tabular-nums text-primary">{new Date(event.starts_at).getDate()}</span>
        </div>
        <div className="min-w-0 flex-1 basis-52">
          <div className="flex flex-wrap items-center gap-2">
            <strong className="truncate text-primary">{event.title}</strong>
            <StatusTag status={event.status} />
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted">
            <span className="font-medium tabular-nums text-secondary">{when(event.starts_at)}</span>
            {event.place && <span>in {event.place}</span>}
            {event.source === "hoerer" && <span className="badge badge-info">von einem Hörer</span>}
            {event.series_id && <span className="badge badge-neutral">Teil einer Serie</span>}
          </div>
        </div>
        <button className={`btn btn-sm ${open ? "btn-secondary" : "btn-ghost"}`} onClick={onToggle}>
          {open ? "Zuklappen" : "Ansehen"}
        </button>
      </div>

      {event.reject_reason && <p className="mt-2 text-sm text-red-700 dark:text-red-300">Abgelehnt: {event.reject_reason}</p>}

      {open && (
        <div className="mt-4 space-y-3 sm:pl-[3.75rem]">
          <dl className="grid gap-x-4 gap-y-1.5 text-sm text-primary sm:grid-cols-[8rem_1fr]">
            {event.ends_at && <><dt className="text-muted">Ende</dt><dd>{when(event.ends_at)}</dd></>}
            {event.address && <><dt className="text-muted">Adresse</dt><dd>{event.address}</dd></>}
            {event.organizer && <><dt className="text-muted">Veranstalter</dt><dd>{event.organizer}</dd></>}
            {event.admission && <><dt className="text-muted">Eintritt</dt><dd>{event.admission}</dd></>}
            {event.category && <><dt className="text-muted">Kategorie</dt><dd>{event.category}</dd></>}
            {event.link && <><dt className="text-muted">Link</dt><dd className="break-all">{event.link}</dd></>}
            {event.description && <><dt className="text-muted">Beschreibung</dt><dd className="whitespace-pre-wrap">{event.description}</dd></>}
          </dl>

          <div className="well text-sm">
            <div className="section-label mb-1">Kontakt für Rückfragen – nicht öffentlich</div>
            {event.contact_cleared ? (
              <p className="text-muted">Nach dem Termin gelöscht.</p>
            ) : event.contact_name || event.contact_mail || event.contact_phone ? (
              <p>{[event.contact_name, event.contact_mail, event.contact_phone].filter(Boolean).join(" · ")}</p>
            ) : (
              <p className="text-muted">Keine Angabe.</p>
            )}
          </div>

          <div className="flex flex-wrap gap-1.5">
            {can("approve") && (
              <button disabled={busy} className="btn btn-primary btn-sm"
                onClick={() => void run(() => eventsApi.act(event.id, "approve"), "Termin freigegeben – er steht jetzt öffentlich.")}>
                Freigeben
              </button>
            )}
            {can("reject") && (
              <button disabled={busy} className="btn btn-secondary btn-sm"
                onClick={() => setRejecting(!rejecting)}>
                Ablehnen
              </button>
            )}
            {can("retract") && (
              <button disabled={busy} className="btn btn-secondary btn-sm"
                onClick={() => void run(() => eventsApi.act(event.id, "retract"), "Termin zurückgezogen.")}>
                Zurückziehen
              </button>
            )}
            <button disabled={busy} className="btn btn-secondary btn-sm"
              onClick={() => void eventsApi.duplicates(event.id).then(setDupes).catch((err) => onError(errorText(err)))}>
              Doppelte suchen
            </button>
            {overview.may_edit && (
              <button disabled={busy} className="btn btn-ghost btn-sm text-red-600 dark:text-red-400"
                onClick={() => void run(
                  () => eventsApi.remove(event.id),
                  event.was_public ? "Termin abgesagt – wer ihn abonniert hat, sieht das im eigenen Kalender." : "Termin gelöscht."
                )}>
                {event.was_public ? "Absagen" : "Löschen"}
              </button>
            )}
          </div>

          {rejecting && (
            <div className="well space-y-2">
              <label className="block text-sm">
                <span className="field-label">Warum kommt der Termin nicht? Das steht später hier im Eingang.</span>
                <input className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={400} />
              </label>
              <button disabled={busy || reason.trim() === ""} className="btn btn-primary btn-sm"
                onClick={() => void run(() => eventsApi.act(event.id, "reject", { reason: reason.trim() }), "Termin abgelehnt.")}>
                Ablehnen und Grund merken
              </button>
            </div>
          )}

          {dupes !== null && (
            <div className="well text-sm">
              {dupes.length === 0 ? (
                <p className="text-muted">Nichts gefunden, was dasselbe sein könnte.</p>
              ) : (
                <>
                  <p className="mb-1 text-muted">Könnte dasselbe sein – zusammenführen lässt den anderen stehen:</p>
                  <ul className="space-y-1">
                    {dupes.map((d) => (
                      <li key={d.id} className="flex flex-wrap items-baseline gap-2">
                        <span className="tabular-nums text-muted">{when(d.starts_at)}</span>
                        <span>{d.title}</span>
                        <span className="badge badge-neutral tabular-nums">{d.similarity}% ähnlich</span>
                        <button disabled={busy} className="btn btn-secondary btn-sm"
                          onClick={() => void run(() => eventsApi.merge(event.id, d.id), "Zusammengeführt – dieser Termin zeigt jetzt auf den anderen.")}>
                          Diesen hier behalten
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}

          {overview.may_edit && (
            <div className="card p-4">
              <div className="section-label mb-3">Bearbeiten</div>
              <EventForm
                categories={overview.categories}
                value={inputOf(event)}
                onCancel={onToggle}
                onSave={async (input) => {
                  await eventsApi.save(event.id, input);
                  onDone("Termin gespeichert.");
                }}
                onError={onError}
              />
            </div>
          )}
        </div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------- Formular

function EventForm({ categories, value, onSave, onCancel, onError }: {
  categories: EventCategory[];
  value: EventInput;
  onSave: (input: EventInput) => Promise<void>;
  onCancel: () => void;
  onError: (message: string) => void;
}): JSX.Element {
  const [draft, setDraft] = useState<EventInput>(value);
  const [busy, setBusy] = useState(false);
  const set = (patch: Partial<EventInput>): void => setDraft({ ...draft, ...patch });

  const save = async (): Promise<void> => {
    setBusy(true);
    try {
      await onSave(draft);
    } catch (err) {
      onError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm">
        <span className="field-label">Was ist los?</span>
        <input className={inputClass} value={draft.title} onChange={(e) => set({ title: e.target.value })} maxLength={120} />
      </label>
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="block text-sm">
          <span className="field-label">Tag</span>
          <input type="date" className={inputClass} value={draft.date} onChange={(e) => set({ date: e.target.value })} />
        </label>
        <label className="block text-sm">
          <span className="field-label">Beginn</span>
          <input type="time" className={inputClass} value={draft.time} onChange={(e) => set({ time: e.target.value })} />
        </label>
        <label className="block text-sm">
          <span className="field-label">Ende (freiwillig)</span>
          <input type="time" className={inputClass} value={draft.end_time ?? ""} onChange={(e) => set({ end_time: e.target.value })} />
        </label>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="field-label">Ort</span>
          <input className={inputClass} value={draft.place ?? ""} onChange={(e) => set({ place: e.target.value })} maxLength={80} />
        </label>
        <label className="block text-sm">
          <span className="field-label">Adresse</span>
          <input className={inputClass} value={draft.address ?? ""} onChange={(e) => set({ address: e.target.value })} maxLength={160} />
        </label>
        <label className="block text-sm">
          <span className="field-label">Veranstalter</span>
          <input className={inputClass} value={draft.organizer ?? ""} onChange={(e) => set({ organizer: e.target.value })} maxLength={120} />
        </label>
        <label className="block text-sm">
          <span className="field-label">Kategorie</span>
          <select className={inputClass} value={draft.category_id ?? ""} onChange={(e) => set({ category_id: e.target.value || null })}>
            <option value="">keine</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label className="block text-sm">
          <span className="field-label">Eintritt</span>
          <input className={inputClass} value={draft.admission ?? ""} onChange={(e) => set({ admission: e.target.value })} maxLength={80} placeholder="frei, 5 Euro, …" />
        </label>
        <label className="block text-sm">
          <span className="field-label">Link</span>
          <input className={inputClass} value={draft.link ?? ""} onChange={(e) => set({ link: e.target.value })} maxLength={300} placeholder="https://…" />
        </label>
      </div>
      <label className="block text-sm">
        <span className="field-label">Kurz beschrieben</span>
        <textarea className={inputClass} rows={3} value={draft.description ?? ""} onChange={(e) => set({ description: e.target.value })} maxLength={1500} />
      </label>
      <div className="flex gap-2 pt-1">
        <button disabled={busy || draft.title.trim().length < 3 || !draft.date || !draft.time}
          className="btn btn-primary"
          onClick={() => void save()}>
          Speichern
        </button>
        <button className="btn btn-ghost" onClick={onCancel}>Abbrechen</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Kategorien

function CategoriesTab({ overview, onDone, onError }: TabProps): JSX.Element {
  const [name, setName] = useState("");

  const add = async (): Promise<void> => {
    try {
      await eventsApi.addCategory(name.trim());
      setName("");
      onDone("Kategorie angelegt.");
    } catch (err) {
      onError(errorText(err));
    }
  };

  return (
    <div className="space-y-3">
      <p className="max-w-3xl text-sm text-muted">
        Kategorien helfen beim Filtern auf der Hörerseite – „Fest“, „Konzert“, „Markt“, „Sport“. Wird eine Kategorie
        gelöscht, bleiben ihre Termine stehen; sie haben dann eben keine.
      </p>
      <div className="flex flex-wrap gap-2">
        <input className={`${inputClass} min-w-0 max-w-xs flex-1`} value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="Neue Kategorie" />
        <button disabled={name.trim() === ""} className="btn btn-primary" onClick={() => void add()}>
          Anlegen
        </button>
      </div>
      {overview.categories.length === 0 ? (
        <EmptyState title="Noch keine Kategorie." />
      ) : (
        <ul className="card divide-y divide-line overflow-hidden">
          {overview.categories.map((c) => (
            <li key={c.id} className="flex items-center gap-2 px-4 py-3 hover:bg-surface-hover">
              <span className="flex-1 font-medium text-primary">{c.name}</span>
              <span className="badge badge-neutral tabular-nums">{c.events ?? 0} Termine</span>
              <button className="btn btn-ghost btn-sm text-red-600 dark:text-red-400"
                onClick={() => void eventsApi.removeCategory(c.id).then(() => onDone("Kategorie gelöscht.")).catch((err) => onError(errorText(err)))}>
                Löschen
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Serien

function SeriesTab({ overview, onDone, onError }: TabProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fields, setFields] = useState<EventInput>(emptyInput());
  const [rule, setRule] = useState<EventRule>({
    freq: "monatlich", interval: 1, weekday: 5, nth: 1,
    start_date: "", time: "", duration_minutes: 0, until: "",
  });

  const ready = fields.title.trim().length >= 3 && rule.start_date !== "" && rule.time !== "";

  const create = async (): Promise<void> => {
    setBusy(true);
    try {
      const result = await eventsApi.createSeries({
        title: fields.title, place: fields.place, address: fields.address, organizer: fields.organizer,
        description: fields.description, admission: fields.admission, link: fields.link,
        category_id: fields.category_id, rule,
      });
      setOpen(false);
      setFields(emptyInput());
      onDone(result.message);
    } catch (err) {
      onError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="max-w-3xl text-sm text-muted">
        Für alles, was regelmäßig stattfindet: „jeden ersten Freitag im Monat“, „alle zwei Wochen dienstags“. Aus der
        Regel entstehen sofort richtige Termine – einzelne davon könnt ihr später ändern oder absagen, ohne die anderen
        anzufassen. Die Uhrzeit bleibt auch über die Zeitumstellung hinweg dieselbe.
      </p>
      <button className="btn btn-primary" onClick={() => setOpen(!open)}>
        {open ? "Abbrechen" : "Serie anlegen"}
      </button>

      {open && (
        <div className="card animate-fade-in space-y-3 p-5">
          <label className="block text-sm">
            <span className="field-label">Was ist los?</span>
            <input className={inputClass} value={fields.title} onChange={(e) => setFields({ ...fields, title: e.target.value })} maxLength={120} />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="field-label">Ort</span>
              <input className={inputClass} value={fields.place ?? ""} onChange={(e) => setFields({ ...fields, place: e.target.value })} maxLength={80} />
            </label>
            <label className="block text-sm">
              <span className="field-label">Veranstalter</span>
              <input className={inputClass} value={fields.organizer ?? ""} onChange={(e) => setFields({ ...fields, organizer: e.target.value })} maxLength={120} />
            </label>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="block text-sm">
              <span className="field-label">Wie oft</span>
              <select className={inputClass} value={rule.freq} onChange={(e) => setRule({ ...rule, freq: e.target.value as EventRule["freq"] })}>
                <option value="woechentlich">wöchentlich</option>
                <option value="monatlich">monatlich</option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="field-label">Abstand</span>
              <input type="number" min={1} max={12} className={inputClass} value={rule.interval}
                onChange={(e) => setRule({ ...rule, interval: Number(e.target.value) || 1 })} />
            </label>
            {rule.freq === "monatlich" && (
              <label className="block text-sm">
                <span className="field-label">Der wievielte</span>
                <select className={inputClass} value={rule.nth} onChange={(e) => setRule({ ...rule, nth: Number(e.target.value) })}>
                  <option value={1}>erste</option>
                  <option value={2}>zweite</option>
                  <option value={3}>dritte</option>
                  <option value={4}>vierte</option>
                  <option value={-1}>letzte</option>
                </select>
              </label>
            )}
            <label className="block text-sm">
              <span className="field-label">Wochentag</span>
              <select className={inputClass} value={rule.weekday} onChange={(e) => setRule({ ...rule, weekday: Number(e.target.value) })}>
                {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
              </select>
            </label>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="block text-sm">
              <span className="field-label">Ab wann</span>
              <input type="date" className={inputClass} value={rule.start_date} onChange={(e) => setRule({ ...rule, start_date: e.target.value })} />
            </label>
            <label className="block text-sm">
              <span className="field-label">Uhrzeit</span>
              <input type="time" className={inputClass} value={rule.time} onChange={(e) => setRule({ ...rule, time: e.target.value })} />
            </label>
            <label className="block text-sm">
              <span className="field-label">Dauer (Minuten)</span>
              <input type="number" min={0} max={1440} className={inputClass} value={rule.duration_minutes}
                onChange={(e) => setRule({ ...rule, duration_minutes: Number(e.target.value) || 0 })} />
            </label>
            <label className="block text-sm">
              <span className="field-label">Bis wann (freiwillig)</span>
              <input type="date" className={inputClass} value={rule.until} onChange={(e) => setRule({ ...rule, until: e.target.value })} />
            </label>
          </div>
          <p className="text-sm text-muted">Ohne Ende werden zwei Jahre vorausgeplant.</p>
          <button disabled={busy || !ready} className="btn btn-primary" onClick={() => void create()}>
            Serie anlegen
          </button>
        </div>
      )}

      {overview.series.length === 0 ? (
        <EmptyState title="Noch keine Serie." />
      ) : (
        <ul className="card divide-y divide-line overflow-hidden">
          {overview.series.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-2 px-4 py-3 hover:bg-surface-hover">
              <strong className="text-primary">{s.title}</strong>
              <span className="badge badge-brand">{s.label}</span>
              <span className="badge badge-neutral tabular-nums">{s.events} Termine</span>
              <span className="flex-1" />
              <button className="btn btn-ghost btn-sm text-red-600 dark:text-red-400"
                onClick={() => void eventsApi.removeSeries(s.id)
                  .then(() => onDone("Serie beendet. Was schon öffentlich stand, ist abgesagt; Vergangenes bleibt stehen."))
                  .catch((err) => onError(errorText(err)))}>
                Serie beenden
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Für die Sendung

function BroadcastTab({ onError }: { onError: (message: string) => void }): JSX.Element {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const inAWeek = useMemo(() => new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10), []);
  const [von, setVon] = useState(today);
  const [bis, setBis] = useState(inAWeek);
  const [text, setText] = useState<string | null>(null);
  const [count, setCount] = useState(0);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    eventsApi.announce(von, bis)
      .then((d) => { setText(d.text); setCount(d.count); setCopied(false); })
      .catch((err) => onError(errorText(err)));
  }, [von, bis, onError]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <p className="max-w-3xl text-sm text-muted">
        Der Kalenderblock für die Sendung: ein fertiger Text zum Ausdrucken oder zum Ablesen im Studio. Kein
        Sprachroboter – das liest ein Mensch vor.
      </p>
      <div className="card flex flex-wrap items-end gap-2 p-3 sm:p-4">
        <label className="text-sm">
          <span className="field-label">Von</span>
          <input type="date" className={inputClass} value={von} onChange={(e) => setVon(e.target.value)} />
        </label>
        <label className="text-sm">
          <span className="field-label">Bis</span>
          <input type="date" className={inputClass} value={bis} onChange={(e) => setBis(e.target.value)} />
        </label>
        <button className="btn btn-secondary" onClick={load}>Neu holen</button>
        <button
          className="btn btn-primary"
          disabled={!text}
          onClick={() => {
            if (!text) return;
            void navigator.clipboard.writeText(text).then(() => setCopied(true)).catch(() => onError("Kopieren hat nicht geklappt."));
          }}
        >
          {copied ? "Kopiert" : "Text kopieren"}
        </button>
        <button className="btn btn-ghost" onClick={() => window.print()}>Drucken</button>
      </div>
      <p className="text-sm tabular-nums text-muted">{count === 1 ? "Ein Termin" : `${count} Termine`} in diesem Zeitraum.</p>
      <pre className="card overflow-x-auto whitespace-pre-wrap p-5 font-sans text-sm leading-relaxed text-primary">{text ?? "Wird geladen …"}</pre>
    </div>
  );
}
